import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
    DashIcon,
    MktgIcon,
    SettingsIcon,
    SupportIcon,
    WebsiteIcon,
    FansIcon,
    FansIconPath,
    DashboardIconPath,
    MarketingIconPath,
    WebsiteIconPath,
    SettingsIconPath,
    GiftIcon,
    GiftIconPath,
    SupportIconPath,
    LearnIcon,
    LearnIconPath,
    TeamIconPath,
    TeamIcon
} from "../svg-icons"
import MobileNavigation from "./MobileNavigation";
import DesktopNavigation from "./DesktopNavigation";
import PublicNavigation from "./PublicNavigation";
import Intercom from "helpers/Intercom";
import useBrand from 'Hooks/useBrand';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useAppView from 'Hooks/useAppView';
import useScreen from "Hooks/useScreen";
import { useNavbar } from "Hooks/useNavbar";
import { useHistory, useLocation } from "react-router-dom";
import { clsx } from "modules/Utils";
import { ExternalSiteLinks, NavigationItems } from "modules/Const";
import useMaestroChat from "Hooks/useMaestroChat";

export const showIntercom = () => {
    // @ts-ignore
    Intercom.open();
}

export const showNewMessageIntercom = () => {
    // @ts-ignore
    Intercom.showNewMessage();
}

// Simplified interface for public navigation (marketing site)
export interface PublicNavigationItem {
    name: string;
    icon?: string;
    iconComponent?: React.ComponentType<{ color?: string; height?: string; width?: string }>;
    iconPath?: React.ReactElement;
    onClick?: (e: any) => void;
    href?: string;
    alternateHrefs?: string[];
    dropdownItems?: PublicNavigationItem[];
    dropdownStyle?: 'boxes' | 'list';
}

// Full interface for authenticated navigation (app navigation)
export interface NavigationItem {
    name: string;
    smallName?: string;
    iconComponent?: React.ComponentType<{ color?: string; height?: string; width?: string }>;
    onClick?: (e: any) => void;
    href?: string;
    alternateHrefs?: string[];
    iconPath?: React.ReactElement,
    dropdownItems?: NavigationItem[]
    showOnMobileMainMenu: boolean;
    showOnDesktopMainMenu: boolean;
    showOnMobileSubMenu: boolean;
    showOnDesktopSubMenu: boolean;
    icon?: string;
    svgIconViewbox?: string;
    toolkitItems?: {
        name: string;
        href: string;
        icon: string;
        description: string;
        color: string;
        brandOnly: boolean;
    }[];
    dropdownStyle?: 'boxes' | 'list';
}

interface NavbarProps {
    className: string;
}

const Navbar = ({
    className,
}: NavbarProps) => {
    const history = useHistory();
    const location = useLocation();
    const { mobileView } = useScreen();
    const { isChatOpen } = useMaestroChat();
    const { shouldHideNavbar } = useNavbar();
    const { currentBrand, isPaidBrand } = useBrand()
    const { organization } = useOrganizationV2();
    const { appView, isTeamView } = useAppView();
    const hasOrganization = (organization && organization.name) || (currentBrand?.isFromAdminSearch && currentBrand?.organizationName);

    // Auth0 hooks for authentication
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const isPublic = !isAuthenticated;

    const handleSignUp = () => {
        loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup'
            }
        });
    };

    const handleLogin = () => {
        loginWithRedirect();
    };

    // Public navigation items (shown when NOT logged in)
    const publicNavigationItems: PublicNavigationItem[] = [
        {
            name: "Platform",
            icon: "marketing",
            dropdownStyle: 'boxes',
            dropdownItems: [
                {
                    name: NavigationItems.MAESTRO,
                    href: "/",
                    icon: "maestroBadgeImage",
                },
                {
                    name: NavigationItems.WEBSITE_CREATION,
                    onClick: () => {
                        window.open(ExternalSiteLinks.SOLUTIONS_WEBSITE_CREATION, '_blank');
                    },
                    icon: "website",
                },
                {
                    name: NavigationItems.AUTOMATED_ADS,
                    onClick: () => {
                        window.open(ExternalSiteLinks.SOLUTIONS_AUTOMATED_MARKETING, '_blank');
                    },
                    icon: "marketing",
                },
                {
                    name: NavigationItems.CROSS_PLATFORM_ANALYTICS,
                    onClick: () => {
                        window.open(ExternalSiteLinks.SOLUTIONS_CROSS_PLATFORM_ANALYTICS, '_blank');
                    },
                    icon: "dashboard",
                },
                {
                    name: NavigationItems.FAN_GROWTH_INSIGHTS,
                    onClick: () => {
                        window.open(ExternalSiteLinks.SOLUTIONS_CROSS_PLATFORM_ANALYTICS, '_blank');
                    },
                    icon: "audiences",
                },
            ]
        },
        {
            name: "About Us",
            onClick: () => {
                window.open(ExternalSiteLinks.ABOUT, '_self');
            },
            icon: "help-center",
        },
        {
            name: "Pricing",
            href: "/pricing",
            icon: "marketing",
        },
        {
            name: "Resources",
            href: "/learn",
            alternateHrefs: [],
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <LearnIcon {...props} />,
            iconPath: <LearnIconPath />,
            icon: "learn",
            dropdownStyle: 'list',
            dropdownItems: [
                {
                    name: "Blog",
                    onClick: () => {
                        window.open(ExternalSiteLinks.BLOG, '_blank');
                    },
                    icon: "learn",
                },
                {
                    name: "Case Studies",
                    onClick: () => {
                        window.open(ExternalSiteLinks.CASE_STUDIES, '_blank');
                    },
                    icon: "learn",
                },
                {
                    name: "Help Center",
                    onClick: () => {
                        window.open(ExternalSiteLinks.HELP_CENTER, '_blank');
                    },
                    icon: "help-center",
                },
                {
                    name: "Video Walkthroughs",
                    onClick: () => {
                        window.open(ExternalSiteLinks.SOCIAL_YOUTUBE, '_blank');
                    },
                    icon: "videos",
                },
            ]
        },
        {
            name: "Log In",
            onClick: handleLogin,
            icon: "audiences",
        },
        {
            name: "Get Started",
            onClick: handleSignUp,
            icon: "user",
        },
    ];

    // Generate navigation items based on app view
    const getNavigationItems = (): NavigationItem[] => {
        if (isTeamView()) {
            // Team navigation items
            return [
                {
                    name: "Home",
                    href: "/team/dashboard",
                    alternateHrefs: ["/"],
                    icon: "home",
                    showOnMobileMainMenu: true,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Marketing",
                    href: "/marketing",
                    alternateHrefs: ["/marketing"],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <MktgIcon {...props} />,
                    iconPath: <MarketingIconPath />,
                    icon: "marketing",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Website",
                    href: "/website",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <WebsiteIcon {...props} />,
                    iconPath: <WebsiteIconPath />,
                    icon: "website",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                // {
                //     name: "Connections",
                //     href: "/team/connections",
                //     alternateHrefs: [],
                //     iconComponent: (props: { color?: string, height?: string, width?: string }) => <DashIcon {...props} />,
                //     iconPath: <DashboardIconPath />,
                //     icon: "dashboard",
                //     showOnMobileMainMenu: false,
                //     showOnDesktopMainMenu: true,
                //     showOnMobileSubMenu: false,
                //     showOnDesktopSubMenu: false,
                // },
                {
                    name: "Team",
                    href: "/team/users",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <TeamIcon {...props} />,
                    iconPath: <TeamIconPath />,
                    icon: "user",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                // {
                //     name: "Fans",
                //     href: "/fans",
                //     alternateHrefs: [],
                //     iconComponent: (props: { color?: string, height?: string, width?: string }) => <FansIcon {...props} />,
                //     iconPath: <FansIconPath />,
                //     icon: "audiences",
                //     showOnMobileMainMenu: false,
                //     showOnDesktopMainMenu: true,
                //     showOnMobileSubMenu: false,
                //     showOnDesktopSubMenu: false,
                // },
                {
                    name: "Settings",
                    href: "/settings",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <SettingsIcon {...props} />,
                    iconPath: <SettingsIconPath />,
                    icon: "gear",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                }
            ];
        } else {
            // Regular navigation items for non-team users
            return [
                // Maestro as the first main tab for authenticated users
                {
                    name: "Maestro",
                    href: "/",
                    icon: "maestroBadgeImage",
                    showOnMobileMainMenu: true,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Marketing",
                    href: "/marketing",
                    alternateHrefs: ["/marketing"],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <MktgIcon {...props} />,
                    iconPath: <MarketingIconPath />,
                    icon: "marketing",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Website",
                    href: "/website",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <WebsiteIcon {...props} />,
                    iconPath: <WebsiteIconPath />,
                    icon: "website",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Dashboard",
                    href: "/dashboard",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <DashIcon {...props} />,
                    iconPath: <DashboardIconPath />,
                    icon: "dashboard",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Fans",
                    href: "/fans",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <FansIcon {...props} />,
                    iconPath: <FansIconPath />,
                    icon: "audiences",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: false,
                    showOnDesktopSubMenu: false,
                },
                {
                    name: "Users",
                    href: "/users",
                    alternateHrefs: [],
                    iconComponent: (props: { color?: string, height?: string, width?: string }) => <TeamIcon {...props} />,
                    iconPath: <TeamIconPath />,
                    icon: "home",
                    showOnMobileMainMenu: false,
                    showOnDesktopMainMenu: true,
                    showOnMobileSubMenu: true,
                    showOnDesktopSubMenu: false
                },
            ];
        }
    };

    // Base navigation items
    const baseNavigationItems: NavigationItem[] = [
        ...getNavigationItems(),
        // Add new Toolkit navigation item
        {
            name: "Toolkit",
            icon: "wand",
            showOnMobileMainMenu: true,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
            // This will be handled as a special case in MobileNavigation
            toolkitItems: [
                {
                    name: "Ad Campaigns",
                    href: "/marketing",
                    icon: "marketing",
                    description: "Create & manage ads",
                    color: "#FFEDDB", // campaignOrange
                    brandOnly: false,
                },
                {
                    name: "Websites",
                    href: "/website",
                    icon: "website",
                    description: "Build & customize",
                    color: "#D7F6D7", // campaignGreen
                    brandOnly: false,
                },
                {
                    name: "Audience Analytics",
                    href: "/dashboard",
                    icon: "dashboard",
                    description: "Performance insights",
                    color: "#DBFDFC", // campaignBlue
                    brandOnly: true,
                },
                {
                    name: "CRM (Fans)",
                    href: "/fans",
                    icon: "audiences",
                    description: "Grow your fanbase",
                    color: "#F1E0FF", // campaignPurple
                    brandOnly: true,
                }
            ]
        },
        {
            name: "Contact Support",
            iconComponent: (props: { color?: string, height?: string, width?: string }) => <SupportIcon {...props} />,
            iconPath: <SupportIconPath />,
            icon: "support",
            onClick: (e: any) => {
                e.preventDefault()
                showIntercom()
            },
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
    ];

    // Additional items only for brand view (not team view)
    const brandOnlyItems: NavigationItem[] = [
        {
            name: "Refer & Earn",
            smallName: "Refer",
            href: '/refer',
            alternateHrefs: [],
            iconComponent: (props: {}) => <GiftIcon {...props} />,
            iconPath: <GiftIconPath />,
            icon: "gift",
            showOnMobileMainMenu: false,
            showOnDesktopMainMenu: false,
            showOnMobileSubMenu: false,
            showOnDesktopSubMenu: false,
        },
    ];

    // Authenticated navigation items (shown when logged in)
    const authenticatedNavigationItems: NavigationItem[] = [
        ...baseNavigationItems,
        // Only add brand-specific items if not in team view
        ...(isTeamView() ? [] : brandOnlyItems),
    ];

    // Apply brand-specific filters for authenticated users
    let hideNavbar = shouldHideNavbar(location.pathname);


    if (!isPublic && hideNavbar) {
        return null;
    }

    if (isPublic) {
        if (isChatOpen) {
            return null;
        }
        return (
            <PublicNavigation
                className={clsx(className, 'publicNavigationFromNavbar')}
                navigationItems={publicNavigationItems}
            />
        )
    }

    if (mobileView) {
        return (
            <MobileNavigation
                className={clsx(className, 'mobileNavigationFromNavbar')}
                navigationItems={authenticatedNavigationItems}
                isPublic={isPublic}
            />
        )
    }

    return (
        <DesktopNavigation
            className={clsx(className, 'desktopNavigationFromNavbar')}
            navigationItems={authenticatedNavigationItems}
            isPublic={isPublic}
        />
    )
}

export default Navbar
